import React, { ChangeEvent, useEffect, useState } from "react";
import {
    LotTypes,
    PropertIdentifier,
    Provinces,
    BXPProvinces,
    RouteTypes,
    StreetDirections,
    StreetTypes,
    translations,
} from "../../entities/Enums";
import { Address } from "./AddressSearchComponent";
import { LOT_TYPE, PROPERTY_IDENTITFIER, UNIT_TYPE } from "./AddressSearchComponent";
import ErrorElement from "../../commonComponent/ErrorElement";
import { tr } from "date-fns/locale";

export interface AddressDetailsProps {
    id: string;
    addressDetails?: Address;
    lang: 'en' | 'fr';
    noStreetNoAddress: boolean;
    readOnly: boolean;
    allowManualAddress?: boolean;
    allowPartialAddress?: boolean;
    hideAddressDetails: boolean;
    hideUnitNumber?: boolean;
    hideStreetNumberSuffix?: boolean;
    alwaysDisplayProvince?: boolean;
    alwaysDisplayCity?: boolean;
    canadianAddress?: boolean;
    hideCountry?: boolean;
    showCountryField?: boolean;
    isBXPaddress?: boolean;
    hideRuralRoute?: boolean;
    hidePOBox?: boolean;
    isOtherOptional?: boolean;
    hideStreetDirection?: boolean;
    hideStreetType?: boolean;
    hideUnitType?: boolean;
    hideTownship?: boolean;
    hideStreetNumberNameCheckbox?: boolean;
    isPostCodeOptional?: boolean;
    isProvinceOptional?: boolean;
    isMunicipalityOptional?: boolean;
    isStreetNameOptional?: boolean;
    isStreetNumberOptional?: boolean;
    freeTextStreetNumber?: boolean;
    lockStreetFieldsAfterPopulate?: boolean;
    manualAddressChecked?: boolean;
    autoSetCountryFromProvince?: boolean;
    useTouchedForNonBxpErrors?: boolean;
    addressFieldLabels?: { [key: string]: string };
    addressFieldLabelsFRA?: { [key: string]: string };
    hideAddressFieldRequirementIndicators?: { [key: string]: boolean };
    combineStreetFields?: boolean;
    populateUnitInStreetNumber?: boolean;
    errors?: {id: string, message:string}[];
    setAddressDetails: (address: Address) => void;
    setTouchedAddressFields: (string)=>void;
};

const AddressDetails = ({
    id,
    addressDetails,
    lang,
    noStreetNoAddress,
    errors,
    readOnly,
    allowManualAddress,
    allowPartialAddress,
    hideAddressDetails,
    hideUnitNumber,
    isBXPaddress,
    hideStreetNumberSuffix,
    alwaysDisplayProvince,
    alwaysDisplayCity,
    canadianAddress,
    hideCountry,
    showCountryField,
    isOtherOptional,
    hideRuralRoute,
    hidePOBox,
    hideStreetType,
    hideStreetDirection,
    hideUnitType,
    hideTownship,
    isPostCodeOptional,
    isProvinceOptional,
    isMunicipalityOptional,
    isStreetNameOptional,
    isStreetNumberOptional,
    freeTextStreetNumber,
    lockStreetFieldsAfterPopulate,
    manualAddressChecked,
    autoSetCountryFromProvince,
    useTouchedForNonBxpErrors,
    addressFieldLabels,
    addressFieldLabelsFRA,
    hideAddressFieldRequirementIndicators,
    combineStreetFields,
    populateUnitInStreetNumber,
    setAddressDetails,
    setTouchedAddressFields
}: AddressDetailsProps) => {

    
    useEffect(() => {
        const derived = getCountryFromProvinceSelection(addressDetails?.province);
        setCountryValue(derived);
        if (derived && addressDetails) {
            setAddressDetails({ ...addressDetails, country: derived });
        }
    }, [addressDetails?.province]);

       useEffect(() => {
        const derived = getBXPCountryFromProvinceSelection(addressDetails);
        setCountryValue(derived);
        if (derived && addressDetails) {
            setAddressDetails({ ...addressDetails, country: derived });
        }
    }, [addressDetails?.province,addressDetails?.country]);


    const handleIdentifierChange = (e: ChangeEvent<HTMLInputElement>) => {
        setTouchedAddressFields('identifierType')
        if (e.target.checked) {
            setAddressDetails({...addressDetails, identifier: e.target.value as PROPERTY_IDENTITFIER})
        }
    }
    const getCountryFromProvinceSelection = (provinceValue?: string) => {
        if (provinceValue && provinceValue !== 'OTHER') {
            return 'Canada';
        }
        return '';
    }

     const getBXPCountryFromProvinceSelection = (addressDetails: Address) => {
        if (addressDetails?.province && addressDetails.province !== 'OTHER') {
            return 'Canada';
        }
        return addressDetails?.country;
    }
    const countryReadOnly = !!addressDetails?.province && addressDetails.province !== 'OTHER';

    const [countryValue, setCountryValue] = useState<string>(isBXPaddress ? getBXPCountryFromProvinceSelection(addressDetails) : getCountryFromProvinceSelection(addressDetails?.province));

    const [nonBxpTouchedFields, setNonBxpTouchedFields] = useState<{ municipality?: boolean; postalCode?: boolean; }>({});
    
    const enabledSelectStyle = { backgroundColor: '#fff', color: '#1a1a1a', opacity: 1 };
    const shouldLockStreetFields = !!lockStreetFieldsAfterPopulate && !manualAddressChecked;
    const shouldDisableStreetDirection = !isBXPaddress && (
        readOnly || (!!addressDetails?.streetNumber && !!addressDetails?.streetName));
        const getAddressLabel = (key: string, defaultEn: string, defaultFr: string) => {
        const labels = lang === 'en' ? addressFieldLabels : addressFieldLabelsFRA;
        const customLabel = labels?.[key]?.trim();
        return customLabel || (lang === 'en' ? defaultEn : defaultFr);
    }
    const isRequirementIndicatorHidden = (key: string) => !!hideAddressFieldRequirementIndicators?.[key];
    const getRequirementSuffix = (key: string, enText: string, frText: string) => {
        if (isRequirementIndicatorHidden(key)) {
            return '';
        }
        return lang === 'en' ? ` (${enText})` : ` (${frText})`;
    }
    const renderRequirementFlag = (key: string, enText: string, frText: string) => {
        if (isRequirementIndicatorHidden(key)) {
            return null;
        }
        return (
            <span className="ontario-label__flag" style={{ fontWeight: "normal" }}>
                {lang === 'en' ? `(${enText})` : `(${frText})`}
            </span>
        );
    }
    const markNonBxpTouched = (fieldName: 'municipality' | 'postalCode') => {
        if (!useTouchedForNonBxpErrors) return;
        setNonBxpTouchedFields((prev) => ({ ...prev, [fieldName]: true }));
    }
    const shouldShowNonBxpFieldError = (fieldName: 'municipality' | 'postalCode') => {
        const hasError = !!errors?.filter(err => err.id === fieldName).length;
        if (!useTouchedForNonBxpErrors) return hasError;
        return hasError && !!nonBxpTouchedFields[fieldName];
    }

    return (
       addressDetails?.isDetailAddress && isBXPaddress ? (
            <>
                {!hideUnitNumber && <div style={{display: [1].includes(addressDetails?.mailAddressType) || allowManualAddress || allowPartialAddress ?"block":"none"}}>
                    <label className="ontario-label" htmlFor={'fdIpt_unit_number_' + id}>
                        {lang === 'en'
                            ? <>
                                                     {getAddressLabel('unitNumber', 'Unit number', 'Numéro d’unité')} {" "}
                          {renderRequirementFlag('unitNumber', 'optional', 'facultatif')}
                            </>
                            : <>
                                                         {getAddressLabel('unitNumber', 'Unit number', 'Numéro d’unité')} {" "}
                                 {renderRequirementFlag('unitNumber', 'optional', 'facultatif')}
                            </>
                        }
                    </label>
                    {!readOnly && !allowManualAddress && !allowPartialAddress && <ErrorElement
                        display={!!errors.filter(err => err.id === 'suiteNumber').length} 
                        message={errors.filter(err => err.id === 'suiteNumber')?.[0]?.message}
                        id={'fdIpt_unit_number_' + id + '_error'}
                    />}
                    <input
                        className={`ontario-input ontario-input--7-char-width ${!!errors.filter(err => err.id === 'suiteNumber').length ? 'ontario-input__error' : ''}`}
                        id={'fdIpt_unit_number_' + id}
                        key={'fdIpt_unit_number_' + id}
                        value={addressDetails?.suiteNumber || ''}
                        pattern="^[0-9A-Za-z]+$"
                        disabled={readOnly}
                        onChange={(e) => {
                            const val = e.target.value;
                            if (val === '' || /^[0-9A-Za-z]+$/.test(val)) {
                                setAddressDetails({...addressDetails, suiteNumber: e.target.value})};
                            }
                        }                                
                    >
                    </input>
                </div>}
                <div>
                <label className="ontario-label" htmlFor={'fdIpt_street_number_' + id}>
                    {lang === 'en' ? (
                        <>
                            {getAddressLabel('streetNumber', 'Street number', 'Numéro de la rue')} {" "}
                            {renderRequirementFlag('streetNumber', isStreetNumberOptional ? 'optional' : 'required', isStreetNumberOptional ? 'facultatif' : 'obligatoire')}
                        </>
                    ) : (
                        <>
                            {getAddressLabel('streetNumber', 'Street number', 'Numéro de la rue')} {" "}
                            {renderRequirementFlag('streetNumber', isStreetNumberOptional ? 'optional' : 'required', isStreetNumberOptional ? 'facultatif' : 'obligatoire')}
                        </>
                    )}
                </label>
                {!readOnly && <ErrorElement     
                    display={!!errors.filter(err => err.id === 'streetNumber').length} 
                    message={errors.filter(err => err.id === 'streetNumber')?.[0]?.message}
                    id={'fdIpt_street_number_' + id + '_error'}
                />}
                <input
                    className={`ontario-input ${combineStreetFields ? 'ontario-input--30-char-width' : 'ontario-input--10-char-width'} ${!!errors.filter(err => err.id === 'streetNumber').length ? 'ontario-input__error' : ''}`}
                    id={'fdIpt_street_number_' + id}
                    key={'fdIpt_street_number_' + id}
                    value={addressDetails?.streetNumber || ''}
                    required={true}
                    disabled={readOnly || (shouldLockStreetFields && !!addressDetails?.streetNumber)}
                    onChange={(e) => {
                        const val = e.target.value;
                        if (populateUnitInStreetNumber || combineStreetFields || val === '' || /^[0-9]+$/.test(val)) {
                            setAddressDetails({...addressDetails, streetNumber: val});
                        }
                    }}
                    inputMode={(populateUnitInStreetNumber) ? "text" : "numeric"}
                    pattern={(populateUnitInStreetNumber) ? undefined : "[0-9]*"}
                />
                </div>
                <div>
                    <label className="ontario-label" htmlFor={'fdIpt_street_name_' + id}>
                        {lang === 'en' ? (
                            <>
                                {getAddressLabel('streetName', 'Street name', 'Nom de la rue')} {renderRequirementFlag('streetName', isStreetNameOptional ? 'optional' : 'required', isStreetNameOptional ? 'facultatif' : 'obligatoire')}
                            </>
                        ) : (
                            <>
                                {getAddressLabel('streetName', 'Street name', 'Nom de la rue')} {renderRequirementFlag('streetName', isStreetNameOptional ? 'optional' : 'required', isStreetNameOptional ? 'facultatif' : 'obligatoire')}
                            </>
                        )}
                    </label>
                    {!readOnly  && (
                        <ErrorElement
                            display={!!errors.filter(err => err.id === 'streetName').length}
                            message={errors.filter(err => err.id === 'streetName')?.[0]?.message}
                            id={'fdIpt_street_name_' + id + '_error'}
                        />
                    )}
                    <input
                        className={`ontario-input ontario-input--30-char-width ${!!errors.filter(err => err.id === 'streetName').length ? 'ontario-input__error' : ''}`}
                        id={'fdIpt_street_name_' + id}
                        key={'fdIpt_street_name_' + id}
                        value={addressDetails?.streetName || ''}
                        required={true}
                        disabled={readOnly || (shouldLockStreetFields && !!addressDetails?.streetName)}
                        onChange={(e) => setAddressDetails({ ...addressDetails, streetName: e.target.value })}
                        onBlur={(e) => {
                            // Simple required validation on blur
                            if (!e.target.value.trim()) {

                               // addressDetails.streetName = "New Street"; // Mutates object, reference unchanged
                            
                              setAddressDetails({ ...addressDetails, streetName: '' });
                                // Optionally, trigger error handling here if not handled by parent
                            }
                        }}
                    />
                </div>

                {!hideStreetType &&  <div>
                    <label className="ontario-label" htmlFor={'fdIpt_street_type_' + id}>
                        {lang === 'en' ? (
                            <>
                                {getAddressLabel('streetType', 'Street Type', 'Type de rue')} {" "}
                                {renderRequirementFlag('streetType', 'optional', 'facultatif')}
                            </>
                        ) : (
                            <>
                                {getAddressLabel('streetType', 'Street Type', 'Type de rue')} {" "}
                                {renderRequirementFlag('streetType', 'optional', 'facultatif')}
                            </>
                        )}
                    </label>
                            <select
                                className={'ontario-input ontario-dropdown ontario-input--10-char-width'}
                                id={'fdIpt_street_type_' + id}
                                key={'fdIpt_street_type_' + id}
                                value={addressDetails?.streetType?.toUpperCase() || ''}
                                disabled={readOnly || (shouldLockStreetFields && !!addressDetails?.streetType)}
                                aria-readonly={readOnly || (shouldLockStreetFields && !!addressDetails?.streetType)}
                                style={!readOnly ? enabledSelectStyle : null}
                                onChange={(e) => setAddressDetails({ ...addressDetails, streetType: e.target.value })}
                            >
                                {StreetTypes.map((option) => (
                                    <option key={option.value} value={option.value} selected={addressDetails?.streetType?.toLocaleUpperCase()===option.value}> {option.text}</option>
                                ))}
                            </select>
                </div>}

                {!hideStreetDirection && <div>
                    <label className="ontario-label" htmlFor={'fdIpt_street_direction_' + id}>
                        {lang === 'en' ? (
                            <>
                                {getAddressLabel('streetDirection', 'Street Direction', 'Direction de la rue')} {" "}
                                {renderRequirementFlag('streetDirection', 'optional', 'facultatif')}
                            </>
                        ) : (
                            <>
                                {getAddressLabel('streetDirection', 'Street Direction', 'Direction de la rue')} {" "}
                                {renderRequirementFlag('streetDirection', 'optional', 'facultatif')}
                            </>
                        )}
                    </label>
                    <select
                            className={'ontario-input ontario-dropdown ontario-input--10-char-width'}
                            id={'fdIpt_street_direction_' + id}
                            key={'fdIpt_street_direction_' + id}
                            value={addressDetails?.streetDirection || ''}
                            disabled={shouldDisableStreetDirection}
                            autoComplete="off"
                            aria-readonly={shouldDisableStreetDirection}
                            style={!shouldDisableStreetDirection ? enabledSelectStyle : null}
                            onChange={(e) => setAddressDetails({ ...addressDetails, streetDirection: e.target.value })}
                        >
                            {StreetDirections[lang.toUpperCase()].map((option) => (
                                <option key={option.value} value={option.value}> {option.text}</option>
                            ))}
                    </select>
                </div>}

                {!hideRuralRoute && <div>
                    <label className="ontario-label" htmlFor={'fdIpt_manicipality_' + id}>
                        {lang === 'en' ? (
                            <>
                                {getAddressLabel('ruralRoute', 'Rural route', 'Route rurale')} {" "}
                                {renderRequirementFlag('ruralRoute', 'optional', 'facultatif')}
                            </>
                        ) : (
                            <>
                                {getAddressLabel('ruralRoute', 'Rural route', 'Route rurale')} {" "}
                                {renderRequirementFlag('ruralRoute', 'optional', 'facultatif')}
                            </>
                        )}
                    </label>
                        {/* <p>{lang === 'en' ? 'For example Toronto' : 'Par exemple : Toronto'}</p> */}
                        {(!readOnly || allowPartialAddress) && !allowManualAddress && <ErrorElement 
                            display={!!errors.filter(err => err.id === 'ruralRoute').length} 
                            message={errors.filter(err => err.id === 'ruralRoute')?.[0]?.message}
                            id={'fdIpt_manicipality_' + id + '_error'}
                        />}
                        <input
                            className={`ontario-input ontario-input--30-char-width ${!!errors.filter(err => err.id === 'ruralRoute').length ? 'ontario-input__error' : ''}`}
                            id={'fdIpt_manicipality_' + id}
                            key={'fdIpt_manicipality_' + id}
                            value={addressDetails?.ruralRoute || ''}
                            required={true && !allowPartialAddress && !allowManualAddress}
                            readOnly={true && !allowPartialAddress && !allowManualAddress}
                            onChange={(e) => setAddressDetails({...addressDetails, ruralRoute: e.target.value })}
                        />
                </div>}
                {!hidePOBox && <div>

                <label className="ontario-label" htmlFor={'fdIpt_poBoxNumber_' + id}>
                    {lang === 'en' ? (
                        <>
                            {getAddressLabel('poBoxNumber', 'P.O.Box', 'Case postale')} {" "}
                            {renderRequirementFlag('poBoxNumber', 'optional', 'facultatif')}
                        </>
                    ) : (
                        <>
                            {getAddressLabel('poBoxNumber', 'P.O.Box', 'Case postale')} {" "}
                            {renderRequirementFlag('poBoxNumber', 'optional', 'facultatif')}
                        </>
                    )}
                </label>

                {/* <p>{lang === 'en' ? 'For example Toronto' : 'Par exemple : Toronto'}</p> */}
                {(!readOnly || allowPartialAddress) && !allowManualAddress && <ErrorElement 
                    display={!!errors.filter(err => err.id === 'poBoxNumber').length} 
                    message={errors.filter(err => err.id === 'poBoxNumber')?.[0]?.message}
                    id={'fdIpt_manicipality_' + id + '_error'}
                />}
                <input
                    className={`ontario-input ontario-input--30-char-width ${!!errors.filter(err => err.id === 'poBoxNumber').length ? 'ontario-input__error' : ''}`}
                    id={'fdIpt_poBoxNumber_' + id}
                    key={'fdIpt_poBoxNumber_' + id}
                    value={addressDetails?.poBoxNumber || ''}
                    required={true && !allowPartialAddress && !allowManualAddress}
                    readOnly={true && !allowPartialAddress && !allowManualAddress}
                    onChange={(e) => setAddressDetails({...addressDetails, poBoxNumber: e.target.value })}
                />
                </div>}
                <div>
                <label className="ontario-label" htmlFor={'fdIpt_postalCode_' + id}>
                    {lang === 'en' ? (
                        <>
                            {getAddressLabel('postalCode', 'Postal code', 'Code postal')} {" "}
                            {renderRequirementFlag('postalCode', isPostCodeOptional ? 'optional' : 'required', isPostCodeOptional ? 'facultatif' : 'obligatoire')}
                        </>
                    ) : (
                        <>
                            {getAddressLabel('postalCode', 'Postal code', 'Code postal')} {" "}
                            {renderRequirementFlag('postalCode', isPostCodeOptional ? 'optional' : 'required', isPostCodeOptional ? 'facultatif' : 'obligatoire')}
                        </>
                    )}
                </label>
                  
                    {/* <p>{lang === 'en' ? 'For example A1A 1A1' : 'Par exemple : A1A 1A1'}</p> */}
                    {!readOnly  && <ErrorElement 
                        display={!!errors.filter(err => err.id === 'postalCode').length} 
                        message={errors.filter(err => err.id === 'postalCode')?.[0]?.message}
                        id={'fdIpt_postalCode_' + id + '_error'}
                    />}
                    <input
                        className={`ontario-input ontario-input--10-char-width ${!!errors.filter(err => err.id === 'postalCode').length ? 'ontario-input__error': ''}`}
                        id={'fdIpt_postalCode_' + id}
                        key={'fdIpt_postalCode_' + id}
                        value={addressDetails?.postalCode || ''}
                        required={true }
                        readOnly={true && !allowManualAddress}
                     
                        onChange={(e) => {
                            let val = e.target.value.toUpperCase();
                            if (addressDetails.province !=='OTHER' && val.replace(/\s/g, '').length === 4 && !val.includes(' ')) {
                                val = val.slice(0, 3) + ' ' + val.slice(3);
                            }
                            setAddressDetails({ ...addressDetails, postalCode: val });
                        }}
                    />
                </div>
            <div>
            <label className="ontario-label" htmlFor={'fdIpt_manicipality_' + id}>
                {lang === 'en' ? (
                    <>
                        {getAddressLabel('municipality', 'City/Town', 'Ville')} {" "}
                        {renderRequirementFlag('municipality', isMunicipalityOptional ? 'optional' : 'required', isMunicipalityOptional ? 'facultatif' : 'obligatoire')}
                    </>
                ) : (
                    <>
                        {getAddressLabel('municipality', 'City/Town', 'Ville')} {" "}
                        {renderRequirementFlag('municipality', isMunicipalityOptional ? 'optional' : 'required', isMunicipalityOptional ? 'facultatif' : 'obligatoire')}
                    </>
                )}
            </label>
            <ErrorElement
                display={!!errors?.filter(err => err.id === 'municipality').length}
                message={errors?.filter(err => err.id === 'municipality')?.[0]?.message}
                id={'fdIpt_manicipality_' + id + '_error_always'}
            />
            <input
                className="ontario-input ontario-input--30-char-width"
                id={'fdIpt_manicipality_' + id}
                key={'fdIpt_manicipality_' + id}
                value={addressDetails?.municipality || ''}
                readOnly={readOnly}
                onChange={(e) => {
                
                    const updated: Address = {
                        ...(addressDetails || ({} as Address)),
                        municipality: e.target.value,
                        isDetailAddress: true,
                    };
                    setAddressDetails(updated);
                }}
            />
            </div>
            <div>
                <label className="ontario-label" htmlFor={'fdIpt_province_' + id}>
                    {lang === 'en' ? (
                        <>
                            {getAddressLabel('province', 'Province', 'Province')} {" "}
                            {renderRequirementFlag('province', isProvinceOptional ? 'optional' : 'required', isProvinceOptional ? 'optionnel' : 'obligatoire')}
                        </>
                    ) : (
                        <>
                            {getAddressLabel('province', 'Province', 'Province')} {" "}
                            {renderRequirementFlag('province', isProvinceOptional ? 'optional' : 'required', isProvinceOptional ? 'optionnel' : 'obligatoire')}
                        </>
                    )}
                </label>
                     <ErrorElement
                        display={!!errors?.filter(err => err.id === 'province').length}
                        message={errors?.filter(err => err.id === 'province')?.[0]?.message}
                        id={'fdIpt_province_' + id + '_error_always'}
                    />
                    <select
                        className={'ontario-input ontario-dropdown ontario-input--10-char-width'}
                        id={'fdIpt_province_' + id}
                        key={'fdIpt_province_' + id}
                        value={addressDetails?.province || ''}
                        disabled={readOnly}
                        style={!readOnly ? enabledSelectStyle : null}
                        onChange={(e) => {
                            if (readOnly) return;
                            const selectedProvince = e.target.value;
                            const updated: Address = {
                                ...(addressDetails || ({} as Address)),
                                province: selectedProvince,
                                provinceOther: selectedProvince === 'OTHER' ? addressDetails?.provinceOther || '' : null,
                                isDetailAddress: true,
                            };
                            // setCountryValue(getCountryFromProvinceSelection(selectedProvince));
                            setAddressDetails(updated);
                        }}
                    >
                        {BXPProvinces[lang.toUpperCase()].map((option) => (
                            <option
                                key={option.value}
                                value={option.value}
                            >
                                {option.text}
                            </option>
                        ))}

                        {isOtherOptional && (
                            <option value="OTHER">
                                {lang === 'en' ? 'Other' : 'Autre'}
                            </option>
                        )}
                     

                    </select>
                    {addressDetails?.province === 'OTHER' && (
                        <>
                         <ErrorElement
                            display={!!errors?.filter(err => err.id === 'provinceOther').length}
                            message={errors?.filter(err => err.id === 'provinceOther')?.[0]?.message}
                            id={'fdIpt_province_other' + id + '_error_always'}
                        />
                        <div style={{ marginTop: '8px' }}>
                            <label className="ontario-label" htmlFor={'fdIpt_province_other_' + id}>
                                {getAddressLabel('provinceOther', 'Other (please specify)', 'Autre (veuillez préciser)')}
                                {getRequirementSuffix('provinceOther', 'required', 'obligatoire')}
                            </label>
                            <input
                                className="ontario-input ontario-input--30-char-width"
                                id={'fdIpt_province_other_' + id}
                                key={'fdIpt_province_other_' + id}
                                value={addressDetails?.provinceOther || ''}
                                disabled={readOnly}
                                onChange={(e) => {
                                    if (readOnly) return;
                                    const updated: Address = {
                                        ...(addressDetails || ({} as Address)),
                                        province: 'OTHER',
                                        provinceOther: e.target.value,
                                        isDetailAddress: true,
                                    };
                                    setAddressDetails(updated);
                                }}
                            />
                        </div>
                    </>)}
                </div>
               { showCountryField === true && hideCountry !== true && <div>
                    <label className="ontario-label" htmlFor={'fdIpt_country_' + id}>
                        {lang === 'en' ? (
                            <>
                                {getAddressLabel('country', 'Country', 'Pays')} {" "}
                                {renderRequirementFlag('country', 'required', 'obligatoire')}
                            </>
                        ) : (
                            <>
                                {getAddressLabel('country', 'Country', 'Pays')} {" "}
                                {renderRequirementFlag('country', 'required', 'obligatoire')}
                            </>
                        )}
                    </label>
                    {/* <p>{lang === 'en' ? 'For example A1A 1A1' : 'Par exemple : A1A 1A1'}</p> */}
                    {!readOnly  && <ErrorElement 
                        display={!!errors.filter(err => err.id === 'country').length} 
                        message={errors.filter(err => err.id === 'country')?.[0]?.message}
                        id={'fdIpt_country_' + id + '_error'}
                    />}
                    <input
                        className={`ontario-input ontario-input--10-char-width ${!!errors.filter(err => err.id === 'country').length ? 'ontario-input__error': ''}`}
                        id={'fdIpt_country_' + id}
                        key={'fdIpt_country_' + id}
                        value={countryValue}
                        required={true }
                    
                        onChange={(e) => {
                        const newCountry = e.target.value;
                        setCountryValue(newCountry);
                        setAddressDetails({ ...addressDetails, country: newCountry });
                        // Also update field.address if it exists
                        if (addressDetails) {
                            addressDetails.country = newCountry;
                        }
                    }}
                    />
                </div>}
            </>
        ) : (
            <>
            {alwaysDisplayCity && !addressDetails?.isDetailAddress && (
                <div>
                    <label className="ontario-label" htmlFor={'fdIpt_manicipality_' + id}>
                        {getAddressLabel('municipality', 'Municipality', 'Ville')}
                        {getRequirementSuffix('municipality', 'required', 'obligatoire')}
                    </label>
                    <ErrorElement
                        display={!!errors?.filter(err => err.id === 'municipality').length}
                        message={errors?.filter(err => err.id === 'municipality')?.[0]?.message}
                        id={'fdIpt_manicipality_' + id + '_error_always'}
                    />
                    <input
                        className="ontario-input ontario-input--30-char-width"
                        id={'fdIpt_manicipality_' + id}
                        key={'fdIpt_manicipality_' + id}
                        value={addressDetails?.municipality || ''}
                        readOnly={readOnly}
                        onChange={(e) => {
                            if (readOnly) return;
                            const updated: Address = {
                                ...(addressDetails || ({} as Address)),
                                municipality: e.target.value,
                                isDetailAddress: false,
                            };
                            setAddressDetails(updated);
                        }}
                    />
                </div>
            )}

            {alwaysDisplayProvince && !addressDetails?.isDetailAddress && (
                <div>
                    <label className="ontario-label" htmlFor={'fdIpt_province_' + id}>{getAddressLabel('province', 'Province', 'Province')}{getRequirementSuffix('province', 'required', 'obligatoire')}</label>
                    <select
                        className={'ontario-input ontario-dropdown ontario-input--10-char-width'}
                        id={'fdIpt_province_' + id}
                        key={'fdIpt_province_' + id}
                        value={addressDetails?.province || ''}
                        disabled={readOnly}
                        style={!readOnly ? enabledSelectStyle : null}
                        onChange={(e) => {
                            if (readOnly) return;
                            const selectedProvince = e.target.value;
                            const updated: Address = {
                                ...(addressDetails || ({} as Address)),
                                province: selectedProvince,
                                provinceOther: selectedProvince === 'OTHER' ? addressDetails?.provinceOther || '' : null,
                                isDetailAddress: false,
                            };
                            setCountryValue(getCountryFromProvinceSelection(selectedProvince));
                            setAddressDetails(updated);
                        }}
                    >
                        {Provinces[lang.toUpperCase()].map((option) => (
                            <option
                                key={option.value}
                                value={option.value}
                            >
                                {option.text}
                            </option>
                        ))}
                        <option value="OTHER">Other</option>
                    </select>
                    {addressDetails?.province === 'OTHER' && (
                        <div style={{ marginTop: '8px' }}>
                            <label className="ontario-label" htmlFor={'fdIpt_province_other_' + id}>
                                {getAddressLabel('provinceOther', 'Other (please specify)', 'Autre (veuillez préciser)')}
                            </label>
                            <input
                                className="ontario-input ontario-input--30-char-width"
                                id={'fdIpt_province_other_' + id}
                                key={'fdIpt_province_other_' + id}
                                value={addressDetails?.provinceOther || ''}
                                disabled={readOnly}
                                onChange={(e) => {
                                    if (readOnly) return;
                                    const updated: Address = {
                                        ...(addressDetails || ({} as Address)),
                                        province: 'OTHER',
                                        provinceOther: e.target.value,
                                        isDetailAddress: false,
                                    };
                                    setAddressDetails(updated);
                                }}
                            />
                        </div>
                    )}
                </div>
            )}

            {addressDetails?.isDetailAddress && (
                <>
                    {noStreetNoAddress  && (
                        <>
                            <div>
                                <label className="ontario-label" htmlFor={`fdIpt_lot_type_${id}`}>{getAddressLabel('lotType', 'Lot type', 'Type de lot')}{getRequirementSuffix('lotType', 'required', 'obligatoire')}</label>
                                {!readOnly && !allowManualAddress && !allowPartialAddress && <ErrorElement
                                    display={!!errors.filter(err => err.id === 'lotType').length} 
                                    message={errors.filter(err => err.id === 'lotType')?.[0]?.message}
                                    id={`fdIpt_lot_type_${id}_error`}
                                />}
                                <select
                                    className={`ontario-input ontario-dropdown ontario-input--7-char-width ${!!errors.filter(err => err.id === 'lotType').length ? 'ontario-input__error' : ''}`}
                                    id={`fdIpt_lot_type_${id}`}
                                    key={`fdIpt_lot_type_${id}`}
                                    value={addressDetails?.lotType ?? ""}
                                    disabled={readOnly && !allowPartialAddress && !allowManualAddress}
                                    onChange={(e) => {
                                        setTouchedAddressFields('lotType');
                                        setAddressDetails({...addressDetails, lotType: e.target.value as LOT_TYPE});
                                    }}
                                >
                                    <option value="" disabled>{lang === 'en' ? 'Select' : 'Sélectionnez'}</option>
                                    {LotTypes[lang.toUpperCase()].map((option) => (
                                        <option key={option.value} value={option.value} selected={option.value===(addressDetails?.lotType ?? '')}>{option.text}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="ontario-label" htmlFor={`fdIpt_lot_num_${id}`}>
                                    {getAddressLabel('lotNumber', 'Lot/part number', 'Numéro de lot ou de partie de lot')}
                                    {getRequirementSuffix('lotNumber', 'required', 'obligatoire')}
                                </label>
                                <p>{lang === 'en' ? 'For example 4' : 'Par exemple : 4'}</p>
                                {!readOnly && !allowManualAddress && !allowPartialAddress && <ErrorElement
                                    display={!!errors.filter(err => err.id === 'lotNumber').length} 
                                    message={errors.filter(err => err.id === 'lotNumber')?.[0]?.message}
                                    id={`fdIpt_lot_num_${id}_error`}
                                />}
                                <input
                                    className={`ontario-input ontario-input--7-char-width ${!!errors.filter(err => err.id === 'lotNumber').length ? 'ontario-input__error' : ''}`}
                                    id={`fdIpt_lot_num_${id}`}
                                    key={`fdIpt_lot_num_${id}`}
                                    value={addressDetails?.lotNumber || ''}
                                    disabled={readOnly}
                                    onChange={(e) => {
                                        setTouchedAddressFields('lotNumber')
                                        setAddressDetails({...addressDetails, lotNumber: e.target.value });
                                    }}
                                />
                            </div>
                            <div>
                                <label className="ontario-label" htmlFor={`fdIpt_prop_id_${id}`}>
                                    {lang === 'en' ? `${getAddressLabel('propertyIdentifier', 'Property identifier', 'Identifiant de la propriété')} (select one)` : `${getAddressLabel('propertyIdentifier', 'Property identifier', 'Identifiant de la propriété')} (en sélectionner un)`}
                                </label>
                                {!readOnly && !allowManualAddress && !allowPartialAddress && <ErrorElement
                                    display={!!errors.filter(err => err.id === 'identifier').length} 
                                    message={errors.filter(err => err.id === 'identifier')?.[0]?.message}
                                />}
                                <div id="propid_radioButton_1" className={`ontario-radios ${!!errors.filter(err => err.id === 'identifier').length ? 'ontario-radios--error' : ''}`}>
                                    <div className="ontario-radios__item" id="fdIpt_div_concession" title='Concession number options'>
                                        <input 
                                            id={`fdIpt_prop_id_concession_${id}`}
                                            className={`ontario-radios__input ontario-input__error ${!!errors.filter(err => err.id === 'identifier').length ? 'ontario-radios__input ontario-radios__error input__error' : ''}`}
                                            type="radio"
                                            name="property_identifier"
                                            value={PropertIdentifier[lang.toUpperCase()][0].value}
                                            checked={addressDetails?.identifier==='CN'}
                                            title={PropertIdentifier[lang.toUpperCase()][0].text}
                                            disabled={readOnly}
                                            onChange={handleIdentifierChange}
                                        />
                                        <label htmlFor={`fdIpt_prop_id_concession_${id}`} className="ontario-label ontario-radios__label">
                                            {PropertIdentifier[lang.toUpperCase()][0].text}
                                        </label>
                                    </div>
                                    <div className="ontario-radios__item" id="fdIpt_div_plan" title="Plan number option">
                                        <input 
                                            id={`fdIpt_prop_id_plan_${id}`}
                                            className={`ontario-radios__input ${!!errors.filter(err => err.id === 'identifier').length ? 'ontario-input__error ontario-radios__input ontario-radios__error' : ''}`}
                                            type="radio"
                                            name="property_identifier"
                                            value={PropertIdentifier[lang.toUpperCase()][1].value}
                                            title={PropertIdentifier[lang.toUpperCase()][1].text} 
                                            checked={addressDetails?.identifier==='PN'}
                                            disabled={readOnly}
                                            onChange={handleIdentifierChange}
                                        />
                                        <label htmlFor="fdIpt_prop_id_plan" className="ontario-label ontario-radios__label">
                                            {PropertIdentifier[lang.toUpperCase()][1].text}
                                        </label>
                                    </div>
                                    <div>
                                        <p>{lang === 'en' ? 'For example 12345' : 'Par exemple : 12345'}</p>
                                        {!readOnly && !allowManualAddress && !allowPartialAddress && <ErrorElement
                                            display={!!errors.filter(err => err.id === 'identifierNumber').length} 
                                            message={errors.filter(err => err.id === 'identifierNumber')?.[0]?.message}
                                            id={`fdIpt_identifier_number_${id}_error`}
                                        />}
                                        <input
                                            className={`ontario-input ontario-input--7-char-width ${!!errors.filter(err => err.id === 'identifierNumber').length ? 'ontario-input__error' : ''}`}
                                            id={'fdIpt_identifier_number_' + id}
                                            key={'fdIpt_identifier_number_' + id}
                                            value={addressDetails?.identifierNumber || ''}
                                            required={true && !allowManualAddress && !allowPartialAddress}
                                            disabled={readOnly}
                                            onChange={(e) => {
                                                setTouchedAddressFields('identifierNumber');
                                                setAddressDetails({...addressDetails, identifierNumber: e.target.value })}
                                            }
                                        />
                                    </div>
                                </div>
                            </div>
                            {!hideTownship && <div>
                                <label className="ontario-label" htmlFor={`fdIpt_township_${id}`}>{getAddressLabel('township', 'Township', 'Canton')}{getRequirementSuffix('township', 'required', 'obligatoire')}</label>
                                <p>{lang === 'en' ? 'For example Algonquin Highlands' : 'Par exemple : Algonquin Highlands'}</p>
                                {!readOnly && !allowManualAddress && !allowPartialAddress && <ErrorElement
                                    display={!!errors.filter(err => err.id === 'township').length} 
                                    message={errors.filter(err => err.id === 'township')?.[0]?.message}
                                    id={`fdIpt_township_${id}_error`}
                                />}
                                <input
                                    className={`ontario-input ontario-input--7-char-width ${!!errors.filter(err => err.id === 'township').length ? 'ontario-input__error' : ''}`}
                                    id={`fdIpt_township_${id}`}
                                    key={`fdIpt_township_${id}`}
                                    value={addressDetails?.township || ''}
                                    disabled={readOnly}
                                    onChange={(e) => {
                                        setTouchedAddressFields('township')
                                        setAddressDetails({...addressDetails, township: e.target.value });
                                    }}
                                />
                            </div>}
                        </>
                    )}
                  {!hideUnitType && <div style={{display: [1].includes(addressDetails?.mailAddressType)?"block":"none"}}>
                        <label className="ontario-label" htmlFor={`fdIpt_unit_type_${id}`}>{getAddressLabel('unitType', 'Unit type', 'Type d’unité')}{getRequirementSuffix('unitType', 'optional', 'facultatif')}</label>
                        {!readOnly && !allowManualAddress && !allowPartialAddress && <ErrorElement
                            display={!!errors.filter(err => err.id === 'unitType').length} 
                            message={errors.filter(err => err.id === 'unitType')?.[0]?.message}
                            id={`fdIpt_unit_type_${id}_error`}
                        />}
                        <select
                            className={`ontario-input ontario-dropdown ontario-input--7-char-width ${!!errors.filter(err => err.id === 'unitType').length ? 'ontario-input__error' : ''}`}
                            id={`fdIpt_unit_type_${id}`}
                            key={`fdIpt_unit_type_${id}`}
                            value={addressDetails?.unitType || ''}
                            disabled={readOnly}
                            onChange={(e) => {
                                setAddressDetails({...addressDetails, unitType: e.target.value as UNIT_TYPE});
                            }}
                        >
                            {translations[lang.toUpperCase()].map((option) => (
                                <option key={option.value} value={option.value} selected={addressDetails?.unitType===option.value}>{option.text}</option>
                            ))}
                        </select>
                    </div>}
                    {!hideUnitNumber && <div style={{display: [1].includes(addressDetails?.mailAddressType) || allowManualAddress || allowPartialAddress ?"block":"none"}}>
                        <label className="ontario-label" htmlFor={'fdIpt_unit_number_' + id}>
                            {lang === 'en'
                                ? `${getAddressLabel('unitNumber', 'Unit number', 'Numéro d’unité')}${getRequirementSuffix('unitNumber', !!addressDetails?.unitType ? 'required' : 'optional', !!addressDetails?.unitType ? 'obligatoire' : 'facultatif')}`
                                : `${getAddressLabel('unitNumber', 'Unit number', 'Numéro d’unité')}${getRequirementSuffix('unitNumber', !!addressDetails?.unitType ? 'required' : 'optional', !!addressDetails?.unitType ? 'obligatoire' : 'facultatif')}`}
                        </label>
                        {!readOnly && !allowManualAddress && !allowPartialAddress && <ErrorElement
                            display={!!errors.filter(err => err.id === 'suiteNumber').length} 
                            message={errors.filter(err => err.id === 'suiteNumber')?.[0]?.message}
                            id={'fdIpt_unit_number_' + id + '_error'}
                        />}
                        <input
                            className={`ontario-input ontario-input--7-char-width ${!!errors.filter(err => err.id === 'suiteNumber').length ? 'ontario-input__error' : ''}`}
                            id={'fdIpt_unit_number_' + id}
                            key={'fdIpt_unit_number_' + id}
                            value={addressDetails?.suiteNumber || ''}
                            required={!!addressDetails?.unitType}
                            pattern="^[0-9A-Za-z]+$"
                            disabled={readOnly}
                            onChange={(e) => {
                                const val = e.target.value;
                                if (val === '' || /^[0-9A-Za-z]+$/.test(val)) {
                                    setAddressDetails({...addressDetails, suiteNumber: e.target.value})};
                                }
                            }                                
                        >
                        </input>
                    </div>}
                    
                    {(!hideAddressDetails || manualAddressChecked || allowPartialAddress) && (
                        <>
                        {!noStreetNoAddress && (
                            <>
                                <div>
                                    <label className="ontario-label" htmlFor={'fdIpt_street_number_' + id}>{getAddressLabel('streetNumber', 'Street number', 'Numéro de rue')}{getRequirementSuffix('streetNumber', 'required', 'obligatoire')}</label>
                                    {!readOnly && !allowManualAddress && !allowPartialAddress && <ErrorElement     
                                        display={!!errors.filter(err => err.id === 'streetNumber').length} 
                                        message={errors.filter(err => err.id === 'streetNumber')?.[0]?.message}
                                        id={'fdIpt_street_number_' + id + '_error'}
                                    />}
                                    <input
                                        className={`ontario-input ${(populateUnitInStreetNumber) ? 'ontario-input--30-char-width' : 'ontario-input--10-char-width'} ${!!errors.filter(err => err.id === 'streetNumber').length ? 'ontario-input__error' : ''}`}
                                        id={'fdIpt_street_number_' + id}
                                        key={'fdIpt_street_number_' + id}
                                        value={addressDetails?.streetNumber || ''}
                                        required={false && !allowManualAddress && !allowPartialAddress}
                                        disabled={readOnly || (!populateUnitInStreetNumber && shouldLockStreetFields && !!addressDetails?.streetNumber)}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            if (freeTextStreetNumber || populateUnitInStreetNumber) {
                                                setAddressDetails({...addressDetails, streetNumber: val});
                                            } else if (val === '' || /^[0-9]+$/.test(val)) {
                                                setAddressDetails({...addressDetails, streetNumber: val});
                                            }
                                        }}
                                        inputMode={(freeTextStreetNumber || populateUnitInStreetNumber) ? "text" : "numeric"}
                                        pattern={(freeTextStreetNumber || populateUnitInStreetNumber) ? undefined : "[0-9]*"}
                                    />
                                </div>
                                {(!hideStreetNumberSuffix || (allowManualAddress && !hideStreetNumberSuffix)) &&
                                    (<div style={{display: [1,2].includes(addressDetails?.mailAddressType) ? "block" : "none"}}>
                                        <label className="ontario-label" htmlFor={'fdIpt_street_number_suffix_' + id}>
                                            {getAddressLabel('streetNumberSuffix', 'Street number suffix', 'Suffixe du numéro de rue')}{getRequirementSuffix('streetNumberSuffix', 'optional', 'facultatif')}
                                        </label>
                                        <p>{lang === 'en' ? 'For example A or 1/4' : 'Par exemple, A de 1/4'}</p>
                                        <input
                                            className={'ontario-input ontario-input--5-char-width'}
                                            id={'fdIpt_street_number_suffix_' + id}
                                            key={'fdIpt_street_number_suffix_' + id}
                                            value={addressDetails?.streetNumberSuffix || ''}
                                            pattern="^(?:[A-Za-z]|1/2|1/4|3/4)$"
                                            required={false}
                                            disabled={readOnly || (shouldLockStreetFields && !!addressDetails?.streetNumber && !!addressDetails?.streetName)}
                                            onChange={(e) => setAddressDetails({...addressDetails, streetNumberSuffix: e.target.value})}
                                        />
                                    </div>
                                )}
                                {((hideStreetNumberSuffix && addressDetails?.streetNumberSuffix) ) && (
                                    <div style={{display: [1,2].includes(addressDetails?.mailAddressType) ? "block" : "none"}}>
                                        <label className="ontario-label" htmlFor={'fdIpt_street_number_suffix_' + id}>{getAddressLabel('streetNumberSuffix', 'Street number suffix', 'Suffixe du numéro de rue')}{getRequirementSuffix('streetNumberSuffix', 'optional', 'facultatif')}</label>
                                        <p>{lang === 'en' ? 'For example A or 1/4' : 'Par exemple, A de 1/4'}</p>
                                        <input
                                            className={'ontario-input ontario-input--5-char-width'}
                                            id={'fdIpt_street_number_suffix_' + id}
                                            key={'fdIpt_street_number_suffix_' + id}
                                            value={addressDetails?.streetNumberSuffix || ''}
                                            pattern="^(?:[A-Za-z]|1/2|1/4|3/4)$"
                                            required={false}
                                            disabled={readOnly || (shouldLockStreetFields && !!addressDetails?.streetNumber && !!addressDetails?.streetName)}
                                            onChange={(e) => setAddressDetails({...addressDetails, streetNumberSuffix: e.target.value})}
                                        />
                                    </div>
                                )}
                
                                <div>
                                    <label className="ontario-label" htmlFor={'fdIpt_street_name_' + id}>{getAddressLabel('streetName', 'Street name', 'Nom de la rue')}{getRequirementSuffix('streetName', 'required', 'obligatoire')}</label>
                                    <p>{lang === 'en' ? 'For example Main' : 'Par exemple, Principale'}</p>
                                    {!readOnly && !allowManualAddress && !allowPartialAddress &&(
                                        <ErrorElement 
                                            display={!!errors.filter(err => err.id === 'streetName').length} 
                                            message={errors.filter(err => err.id === 'streetName')?.[0]?.message}
                                            id={'fdIpt_street_name_' + id + '_error'}
                                        />
                                    )}
                                    <input
                                        className={'ontario-input  ontario-input--30-char-width'}
                                        id={'fdIpt_street_name_' + id}
                                        key={'fdIpt_street_name_' + id}
                                        value={addressDetails?.streetName || ''}
                                        required={false}
                                        disabled={readOnly || (shouldLockStreetFields && !!addressDetails?.streetName)}
                                        onChange={(e) => setAddressDetails({...addressDetails, streetName: e.target.value })}
                                    />
                                </div>
                                {(!hideStreetNumberSuffix || (allowManualAddress && !hideStreetType)) && <div style={{display: [1,2].includes(addressDetails?.mailAddressType) ? "block" : "none"}}>
                                    <label className="ontario-label" htmlFor={'fdIpt_street_type_' + id}>
                                        {getAddressLabel('streetType', 'Street Type', 'Type de rue')}{getRequirementSuffix('streetType', 'optional', 'obligatoire')}
                                    </label>
                                    <p>{lang === 'en' ? 'For example Boulevard' : 'Par exemple, Boulevard'}</p>
                                    <select
                                        className={'ontario-input ontario-dropdown ontario-input--10-char-width'}
                                        id={'fdIpt_street_type_' + id}
                                        key={'fdIpt_street_type_' + id}
                                        value={addressDetails?.streetType?.toUpperCase() || ''}
                                        disabled={readOnly || (shouldLockStreetFields && !!addressDetails?.streetType)}
                                        aria-readonly={readOnly || (shouldLockStreetFields && !!addressDetails?.streetType)}
                                        style={!readOnly ? enabledSelectStyle : null}
                                        onChange={(e) => setAddressDetails({ ...addressDetails, streetType: e.target.value })}
                                    >
                                        {StreetTypes.map((option) => (
                                            <option key={option.value} value={option.value} selected={addressDetails?.streetType?.toLocaleUpperCase()===option.value}> {option.text}</option>
                                        ))}
                                    </select>
                                </div>}

                                {((hideStreetNumberSuffix && addressDetails?.streetNumberSuffix && !hideStreetDirection) || (allowManualAddress&& !hideStreetDirection)) && <div style={{display: [1,2].includes(addressDetails?.mailAddressType) ? "block" : "none"}}>
                                    <label className="ontario-label" htmlFor={'fdIpt_street_direction_' + id}>{getAddressLabel('streetDirection', 'Street Direction', 'Direction de la rue')}{getRequirementSuffix('streetDirection', 'optional', 'facultatif')}</label>
                                    <select
                                        className={'ontario-input ontario-dropdown ontario-input--10-char-width'}
                                    id={'fdIpt_street_direction_' + id}
                                    key={'fdIpt_street_direction_' + id}
                                    value={addressDetails?.streetDirection || ''}
                                    disabled={shouldDisableStreetDirection}
                                    aria-readonly={shouldDisableStreetDirection}
                                    style={!shouldDisableStreetDirection ? enabledSelectStyle : null}
                                    onChange={(e) => setAddressDetails({ ...addressDetails, streetDirection: e.target.value })}
                                >
                                        {StreetDirections[lang.toUpperCase()].map((option) => (
                                            <option key={option.value} value={option.value}> {option.text}</option>
                                        ))}
                                    </select>
                                </div>}
                                <div style={{display: [2,4].includes(addressDetails?.mailAddressType)?"block":"none"}}>
                                    <label className="ontario-label" htmlFor={'fdIpt_route_type_' + id}>{getAddressLabel('routeType', 'Route type', 'Type d’itinéraire')}{getRequirementSuffix('routeType', 'optional', 'facultatif')}</label>
                                    {!readOnly && !allowManualAddress && !allowPartialAddress && (
                                        <ErrorElement 
                                            display={!!errors.filter(err => err.id === 'routeType').length} 
                                            message={errors.filter(err => err.id === 'routeType')?.[0]?.message}
                                            id={'fdIpt_route_type_' + id + '_error'}
                                        />
                                    )}
                                    <select
                                        className={`ontario-input ontario-dropdown ontario-input--10-char-width ${!!errors.filter(err => err.id === 'routeType').length? 'ontario-input__error' : ''}`}
                                        id={'fdIpt_route_type_' + id}
                                        key={'fdIpt_route_type_' + id}
                                        value={addressDetails?.routeType || ''}
                                        disabled={readOnly}
                                        onChange={(e) => setAddressDetails({ ...addressDetails, routeType: e.target.value })}
                                    >
                                        {RouteTypes[lang.toUpperCase()].map((option) => (
                                            <option key={option.value} value={option.value} selected={addressDetails?.routeType===option.value}> {option.text}</option>
                                        ))}
                                    </select>
                                </div>

                                <div style={{display: [2,4].includes(addressDetails?.mailAddressType)?"block":"none"}}>
                                    <label className="ontario-label" htmlFor={'fdIpt_routeNumber_' + id}>{getAddressLabel('routeNumber', 'Route number', 'Numéro de route')}{getRequirementSuffix('routeNumber', 'optional', 'facultatif')}</label>
                                    <p>{lang === 'en' ? 'For example 30' : 'Par exemple : 30'}</p>
                                    {!readOnly && !allowManualAddress && !allowPartialAddress && (
                                        <ErrorElement 
                                            display={!!errors.filter(err => err.id === 'routeNumber').length} 
                                            message={errors.filter(err => err.id === 'routeNumber')?.[0]?.message}
                                            id={'fdIpt_routeNumber_' + id + '_error'}
                                        />
                                    )}
                                    <input
                                        className={`ontario-input ontario-input--5-char-width ${!!errors.filter(err => err.id === 'routeNumber').length? 'ontario-input__error' : ''}`}
                                        id={'fdIpt_routeNumber_' + id}
                                        key={'fdIpt_routeNumber_' + id}
                                        value={addressDetails?.routeNumber || ''}
                                        required={true && !allowManualAddress && !allowPartialAddress}
                                        disabled={readOnly}
                                        onChange={(e) => setAddressDetails({...addressDetails, routeNumber: e.target.value })}
                                    />
                                </div>
                            </>
                        )}
                        <div>
                            <label className="ontario-label" htmlFor={'fdIpt_manicipality_' + id}>{getAddressLabel('municipality', 'Municipality (city, town, etc.)', 'Ville')}{getRequirementSuffix('municipality', 'required', 'obligatoire')}</label>
                            {/* <p>{lang === 'en' ? 'For example Toronto' : 'Par exemple : Toronto'}</p> */}
                            {(!readOnly || allowPartialAddress) && !allowManualAddress && <ErrorElement 
                                display={shouldShowNonBxpFieldError('municipality')} 
                                message={errors.filter(err => err.id === 'municipality')?.[0]?.message}
                                id={'fdIpt_manicipality_' + id + '_error'}
                            />}
                            <input
                                className={`ontario-input ontario-input--30-char-width ${shouldShowNonBxpFieldError('municipality') ? 'ontario-input__error' : ''}`}
                                id={'fdIpt_manicipality_' + id}
                                key={'fdIpt_manicipality_' + id}
                                value={addressDetails?.municipality || ''}
                                required={true && !allowPartialAddress && !allowManualAddress}
                                readOnly={true && !allowPartialAddress && !allowManualAddress}
                                onBlur={() => markNonBxpTouched('municipality')}
                                onChange={(e) => {
                                    markNonBxpTouched('municipality');
                                    setAddressDetails({...addressDetails, municipality: e.target.value })
                                }}
                            />
                        </div>

                        <div>
                            <label className="ontario-label" htmlFor={'fdIpt_province_' + id}>{getAddressLabel('province', 'Province', 'Province')}{getRequirementSuffix('province', 'required', 'obligatoire')}</label>
                            {/* <p>{lang === 'en' ? 'For example Ontario' : 'Par exemple : Ontario'}</p> */}
                            <ErrorElement
                                display={!!errors?.filter(err => err.id === 'province').length}
                                message={errors?.filter(err => err.id === 'province')?.[0]?.message}
                                id={'fdIpt_province_' + id + '_error_always'}
                            />
                            {(!readOnly || allowPartialAddress) && !allowManualAddress && <ErrorElement 
                                display={!!errors.filter(err => err.id === 'province').length} 
                                message={errors.filter(err => err.id === 'province')?.[0]?.message}
                                id={'fdIpt_province_' + id + '_error'}
                            />}
                            <select
                                className={`ontario-input ontario-dropdown ontario-input--10-char-width ${!!errors.filter(err => err.id === 'province').length ? 'ontario-input__error' : ''}`}
                                id={'fdIpt_province_' + id}
                                key={'fdIpt_province_' + id}
                                value={addressDetails?.province || ''}
                                disabled={true && !allowPartialAddress && !allowManualAddress}
                                aria-readonly={true && !allowPartialAddress && !allowManualAddress}
                                style={(!allowPartialAddress && !allowManualAddress) ? null : enabledSelectStyle}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setCountryValue(getCountryFromProvinceSelection(val));
                                    setAddressDetails({
                                        ...addressDetails,
                                        province: val,
                                        provinceOther: val === 'OTHER' ? addressDetails?.provinceOther || '' : null,
                                    });
                                }}
                            >
                                <option value="" disabled>{lang === 'en' ? 'Select' : 'Sélectionnez'}</option>
                                {Provinces[lang.toUpperCase()].map((option) => (
                                    <option 
                                        disabled ={true && !allowPartialAddress && !allowManualAddress}
                                        key={option.value} 
                                        value={option.value} 
                                        selected={addressDetails?.province===option.value}
                                    > 
                                        {option.text}
                                    </option>
                                ))}
                                {(canadianAddress || alwaysDisplayProvince) && (
                                    <option value="OTHER">Other</option>
                                )}
                            </select>
                            {(canadianAddress || alwaysDisplayProvince) && addressDetails?.province === 'OTHER' && (
                                <div style={{ marginTop: '8px' }}>
                                    <label className="ontario-label" htmlFor={'fdIpt_province_other_' + id}>
                                        {getAddressLabel('provinceOther', 'Other (please specify)', 'Autre (veuillez préciser)')}
                                    </label>
                                    <input
                                        className="ontario-input ontario-input--30-char-width"
                                        id={'fdIpt_province_other_' + id}
                                        key={'fdIpt_province_other_' + id}
                                        value={addressDetails?.provinceOther || ''}
                                        disabled={true && !allowPartialAddress && !allowManualAddress}
                                        onChange={(e) => setAddressDetails({
                                            ...addressDetails,
                                            province: 'OTHER',
                                            provinceOther: e.target.value,
                                        })}
                                    />
                                </div>
                            )}
                        </div>

                        <div>
                            <label className="ontario-label" htmlFor={'fdIpt_postalCode_' + id}>{getAddressLabel('postalCode', 'Postal code', 'Code postal')}{getRequirementSuffix('postalCode', 'required', 'obligatoire')}</label>
                            {/* <p>{lang === 'en' ? 'For example A1A 1A1' : 'Par exemple : A1A 1A1'}</p> */}
                            {!readOnly && !allowManualAddress && !allowPartialAddress && <ErrorElement 
                                display={shouldShowNonBxpFieldError('postalCode')} 
                                message={errors.filter(err => err.id === 'postalCode')?.[0]?.message}
                                id={'fdIpt_postalCode_' + id + '_error'}
                            />}
                            <input
                                className={`ontario-input ontario-input--10-char-width ${shouldShowNonBxpFieldError('postalCode') ? 'ontario-input__error': ''}`}
                                id={'fdIpt_postalCode_' + id}
                                key={'fdIpt_postalCode_' + id}
                                value={addressDetails?.postalCode || ''}
                                required={true && !allowPartialAddress && !allowManualAddress}
                                readOnly={true && !allowPartialAddress && !allowManualAddress}
                                onBlur={() => markNonBxpTouched('postalCode')}
                                onChange={(e) => {
                                    markNonBxpTouched('postalCode');
                                    setAddressDetails({ ...addressDetails, postalCode: e.target.value })
                                }}
                            />
                        </div>

                        {showCountryField && (
                            <div>
                                <label className="ontario-label" htmlFor={'fdIpt_country_' + id}>
                                    {getAddressLabel('country', 'Country', 'Pays')}{getRequirementSuffix('country', 'required', 'obligatoire')}
                                </label>
                                <input
                                    className={'ontario-input ontario-input--10-char-width'}
                                    id={'fdIpt_country_' + id}
                                    key={'fdIpt_country_' + id}
                                    value={countryValue}
                                    required={true}
                                    readOnly={readOnly || countryReadOnly}
                                    onChange={(e) => {
                                        setCountryValue(e.target.value);
                                        setAddressDetails({ ...addressDetails, country: e.target.value });
                                    }}
                                />
                            </div>
                        )}

                    </>)}
                </>
            )}
        </>)
    );
}

export default AddressDetails;
